import React, {useEffect, useState, type FC, useCallback, type CSSProperties} from 'react';
import FocusTrap from 'focus-trap-react';
import s from './ProductQuickAddDesktop.scss';
import {IProduct, IProductOption} from '../../../../../types/galleryTypes';
import classNames from 'classnames';
import {useGlobals, withGlobals} from '../../../../../globalPropsContext';
import {ProductQuickAddContent} from '../ProductQuickAddContent/ProductQuickAddContent';
import {ProductQuickAddDropdownDataHook} from '../ProductQuickAddContent/ProductQuickAddTextualOption/ProductQuickAddDropdown/ProductQuickAddDropdown';
import {
  IProvidedTranslationProps,
  withTranslations,
} from '@wix/wixstores-client-common-components/dist/es/src/outOfIframes/translations';

export const ANIMATION_DURATION = 300;

export const ProductQuickAddDesktopDataHooks = {
  Root: 'product-quick-add-desktop-root',
};
export interface IProductQuickAddDesktopProps extends IProvidedTranslationProps {
  product: IProduct;
  options: IProductOption[];
  productIndex: number;
  productItemRef: React.RefObject<HTMLDivElement>;
  quantity: number;
}

const ProductQuickAddDesktopComp: FC<IProductQuickAddDesktopProps> = ({
  product,
  options,
  productIndex,
  productItemRef,
  quantity,
  t,
}) => {
  const {closeQuickAdd, isEditorMode} = useGlobals();
  const [isAnimatingIn, setIsAnimatingIn] = useState(false);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);

  useEffect(() => {
    setIsAnimatingIn(true);
  }, []);

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      const targetElement = event.target as HTMLElement;
      const isInsideDropdownMenu = targetElement.closest(`[id="${ProductQuickAddDropdownDataHook.Root}"]`);
      const isInsideTheProductItem = productItemRef.current?.contains(targetElement);
      return !isInsideDropdownMenu && !isInsideTheProductItem;
    },
    [productItemRef]
  );

  const handleClose = useCallback(() => {
    setIsAnimatingOut(true);
    setTimeout(() => {
      closeQuickAdd(product.id);
    }, ANIMATION_DURATION);
  }, [closeQuickAdd, product.id]);

  const inlineCssVars = {
    '--quick-add-animation-duration': `${ANIMATION_DURATION}ms`,
  };

  return (
    <div
      data-hook={`${ProductQuickAddDesktopDataHooks.Root}-${productIndex}`}
      className={classNames(s.quickAddDesktopRoot, {
        [s.animateIn]: isAnimatingIn,
        [s.animateOut]: isAnimatingOut,
      })}
      style={inlineCssVars as CSSProperties}
      role="dialog"
      aria-modal="true"
      aria-label={t('quickAddAriaLabel')}>
      <FocusTrap
        active={!isEditorMode}
        focusTrapOptions={{
          allowOutsideClick: true,
          clickOutsideDeactivates: handleClickOutside,
          onDeactivate: handleClose,
          returnFocusOnDeactivate: false,
          fallbackFocus: () => document.body,
        }}>
        <div className={s.quickAddDesktopContent}>
          <ProductQuickAddContent product={product} options={options} quantity={quantity} />
        </div>
      </FocusTrap>
    </div>
  );
};

export const ProductQuickAddDesktop = withGlobals(withTranslations()(ProductQuickAddDesktopComp));
